
// File: src/pages/Lookup.tsx
import React, { useEffect, useState } from 'react';
import { fetchItems, fetchInventory } from '@/api';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, BadgeCheck, AlertTriangle, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { pageTitle } from '@/lib/appConfig';

// Raw item structure from API
interface ItemRaw {
  id: string;
  name: string;
  sku: string;
  selling_price: number;
}
// Raw inventory structure from API
interface InventoryRaw {
  id: string;
  item_id: string;
  quantity: number;
}
// Enriched inventory item for lookup
interface InventoryItem extends ItemRaw {
  quantity: number;
  inStock: boolean;
}

export default function Lookup() {
  useEffect(() => {
    document.title = pageTitle('Lookup');
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<InventoryItem | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');

  const [items, setItems] = useState<ItemRaw[]>([]);
  const [inventory, setInventory] = useState<InventoryRaw[]>([]);

  // Enrich raw items with inventory data
  const enrich = (rawItems: ItemRaw[], inv: InventoryRaw[]): InventoryItem[] =>
    rawItems.map(i => {
      const record = inv.find(x => x.item_id === i.id);
      const qty = record?.quantity ?? 0;
      return { ...i, quantity: qty, inStock: qty > 0 };
    });

  // Load items and inventory once
  useEffect(() => {
    (async () => {
      try {
        const [itemsRes, invRes] = await Promise.all([fetchItems(), fetchInventory()]);
        setItems(itemsRes.data);
        setInventory(invRes.data);
      } catch (err: any) {
        toast.error('Failed to load data');
      }
    })();
  }, []);

  // Handle search
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    const enriched = enrich(items, inventory);
    const found = enriched.find(
      item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (found) {
      setSearchResult(found);
      setNotFound(false);
      setShowCustomForm(false);
    } else {
      setSearchResult(null);
      setNotFound(true);
    }
  };

  // Add custom item action
  const handleAddCustomItem = () => {
    if (!customName.trim() || isNaN(parseFloat(customPrice))) {
      toast.error('Please enter valid item details');
      return;
    }
    toast.success(`Added custom item: ${customName} at $${parseFloat(customPrice).toFixed(2)}`);
    setCustomName('');
    setCustomPrice('');
    setShowCustomForm(false);
    setNotFound(false);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Item Lookup</h1>
        <p className="text-muted-foreground mt-1">
          Search for items in inventory or add custom items.
        </p>
      </div>
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Search className="h-5 w-5 mr-2 text-primary" /> Find Item
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex space-x-2">
            <Input
              placeholder="Enter item name or SKU..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setNotFound(false);
              }}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
            />
            <Button onClick={handleSearch}>Search</Button>
          </div>

          {searchResult && (
            <div className="rounded-md border p-4 bg-background">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center">
                    <h3 className="font-medium">{searchResult.name}</h3>
                    {searchResult.inStock ? (
                      <BadgeCheck className="h-4 w-4 ml-2 text-success" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 ml-2 text-warning" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">SKU: {searchResult.sku}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">${searchResult.selling_price.toFixed(2)}</p>
                  <p className="text-sm text-muted-foreground">
                    {searchResult.inStock ? 'In Stock' : 'Out of Stock'}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Button className="w-full" disabled={!searchResult.inStock}>
                  Add to Bill
                </Button>
              </div>
            </div>
          )}

          {notFound && (
            <div className="rounded-md border border-warning p-4 bg-warning/20">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-warning mr-3" />
                <div>
                  <h3 className="font-medium text-warning">Item not found</h3>
                  <p className="text-sm text-warning/80 mt-1">
                    The item "{searchQuery}" does not exist.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-2"
                    onClick={() => setShowCustomForm(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add Custom Item
                  </Button>
                </div>
              </div>
            </div>
          )}

          {showCustomForm && (
            <div className="rounded-md border p-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="customName">Item Name</Label>
                  <Input
                    id="customName"
                    value={customName}
                    onChange={e => setCustomName(e.target.value)}
                    placeholder="Enter item name"
                  />
                </div>
                <div>
                  <Label htmlFor="customPrice">Price</Label>
                  <Input
                    id="customPrice"
                    type="number"
                    step="0.01"
                    value={customPrice}
                    onChange={e => setCustomPrice(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <Button onClick={handleAddCustomItem} className="w-full">
                  Add to Bill
                </Button>
                <Button variant="outline" className="w-full mt-2" onClick={() => setShowCustomForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="bg-muted/50 flex justify-between text-sm text-muted-foreground">
          <p>Tip: Use the search box or scan a barcode to find items quickly.</p>
        </CardFooter>
      </Card>
    </div>
  );
}
